import React, { useContext, useEffect, useMemo } from 'react'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { Grid, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import InventoryCommodityGrid from 'components/generic/datagrid/InventoryCommodityGrid'
import InventorySummaryBar from 'components/layouts/InventorySummaryBar'
import { useGetInventoryCommodityHandlerQuery } from 'services/inventory'
import { parseNumberObject } from 'utils/helpers'

const InventoryCommodityContainer = () => {
  const context = useContext(ContentLayoutContext)
  const { data, isLoading } = useGetInventoryCommodityHandlerQuery({})
  const { enableFourDecimalPrecisionInInventory } = useSelector(state => state.featureFlags?.variables || {})

  const { defaultYardUm } = useSelector(state => state.settings)

  const summaryItems = useMemo(
    () => [
      {
        label: 'TOTAL WEIGHT',
        value: data?.totalWeight?.amount
          ? parseNumberObject(data?.totalWeight, { format: 'weight', unitOverride: defaultYardUm })
          : '---',
      },
      {
        label: 'TOTAL COST',
        value: parseNumberObject(data?.totalCost, { format: 'value' }),
      },
      {
        label: 'LARGEST MATERIAL BY WEIGHT',
        value: data?.largestMaterialByWeight,
      },
      {
        label: 'LARGEST MATERIAL BY VALUE',
        value: data?.largestMaterialByValue,
      },
      {
        label: 'RECEIVED YESTERDAY',
        value: parseNumberObject(data?.weightReceivedYesterday, { format: 'weight', unitOverride: defaultYardUm }),
      },
      {
        label: 'SHIPPED YESTERDAY',
        value: parseNumberObject(data?.weightShippedYesterday, { format: 'weight', unitOverride: defaultYardUm }),
      },
    ],
    [data],
  )

  const transformedRows = (data?.Items ?? []).map(commodity => ({
    ...commodity,
    id: commodity?.id.toLowerCase(),
    wip: parseNumberObject(commodity?.wip),
    wipFormatted: parseNumberObject(commodity?.wip, { format: 'weight', unitOverride: defaultYardUm }),
    finishedGoods: parseNumberObject(commodity?.finishedGoods),
    finishedGoodsFormatted: parseNumberObject(commodity?.finishedGoods, {
      format: 'weight',
      unitOverride: defaultYardUm,
    }),
    total: parseNumberObject(commodity?.totalWeight),
    totalFormatted: parseNumberObject(commodity?.totalWeight, { format: 'weight', unitOverride: defaultYardUm }),
    averageCost: parseNumberObject(commodity?.averageCost),
    averageCostFormatted: parseNumberObject(commodity?.averageCost, {
      format: 'price',
      enableFourDecimalPrecisionInInventory,
      unitOverride: defaultYardUm,
    }),
    value: parseNumberObject(commodity?.value),
    valueFormatted: parseNumberObject(commodity?.value, { format: 'value' }),
  }))

  useEffect(() => {
    context.setContainerTitle('Inventory')
  }, [])

  return (
    <Stack>
      <InventorySummaryBar items={summaryItems} />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <InventoryCommodityGrid rows={transformedRows} loading={isLoading} />
      </Grid>
    </Stack>
  )
}

export default InventoryCommodityContainer
